import { lazy, useEffect } from 'react';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';

import AiDesign from '../../pages/AI';
import AssignedTests from '../../pages/AssignedTests';
import CloudPrepPage from '../../pages/CloudPrepPage';
import CMSDataEntrySpecialistPage from '../../pages/CMSDataEntrySpecialistPage';
import CreateTestScreen from '../../pages/CustomWrittenTest';
import DriveTestPage from '../../pages/Drive';
import DriveTestResult from '../../pages/DriveTestResult';
import HRInterviewResults from '../../pages/HRInterviewResults';
import InterviewResults from '../../pages/InterviewResults';
import LoginPageV1 from '../../pages/LoginPageV1';
import MockTest from '../../pages/MockTests';
import Otp from '../../pages/Otp';
import PricingPlans from '../../pages/Payments';
import ProgrammingCategories from '../../pages/ProgrammingCategories';
import ProgrammingCategoryResults from '../../pages/ProgrammingCategoryResults';
import ProgrammingResults from '../../pages/ProgrammingResults';
import ProgrammingTest from '../../pages/ProgrammingTest';
import ProgrammingTestV2 from '../../pages/ProgrammingTestV2';
import CampusRecruitmentPage from '../../pages/Qrcode';
import QuestionBankV2 from '../../pages/QuestionBankV2';
import RegistrationPage from '../../pages/RegistrationForm';
import ReportsPage from '../../pages/ReportsPage';
import ResultsPage from '../../pages/ResultsPage';
import ResumeBuilderMainComponent from '../../pages/ResumeBuilderPages/ResumeBuilderMainComponent';
import ScheduleTestManagementDashboard from '../../pages/ScheduleTestManagement';
import StudentRecords from '../../pages/StudentRecords';
import TenantAudit from '../../pages/TenantAudit';
import TransactionSummary from '../../pages/TransactionSummary';
import UserAudits from '../../pages/UserAudits';
import WebsiteLeadsPage from '../../pages/WebsiteLeadsPage';
import { useAppSelector } from '../../redux/store/hooks';
import { getItem } from '../../utils/storage';
import TestScreen from '../cloudPrep/aws/aiTests/TestScreen';
import CloudPrepInstructions from '../cloudPrep/aws/instructions';
import CloudPrepTabs from '../cloudPrep/tabs';
import TestResults from '../Features/TestResults';
import HRRound from '../HrRound';
import SpeakingInterviewApp from '../InterviewRound/InterviewRound';
import MockResults from '../MockTests/MockResults';

import NoAccessPage from './NoAccessPage';
import ProtectedRoute from './ProtectedRoute';

const AuthenticatedLayout = lazy(
  () => import('../../components/Layout/AuthenticatedLayout')
);
const LoginPage = lazy(() => import('../../pages/LoginPage'));
const QuestionBankPage = lazy(() => import('../../pages/QuestionBank'));
const DashboardPage = lazy(() => import('../../pages/Dashboard'));
const PracticeTestLayout = lazy(() => import('../Layout/PracticeTestLayout'));
const OnboardingForm = lazy(() => import('../Features/onBoardingForm'));
const AddQuestionPage = lazy(() => import('../../pages/AddQuestionPage'));
const CMSPage = lazy(() => import('../../pages/CMSPage'));
const OAuthVerification = lazy(() => import('../../pages/OAuthVerification'));
const CMSDataReviewerPage = lazy(
  () => import('../../pages/CMSDataReviewerPage')
);
const CMSAdminPage = lazy(() => import('../../pages/CMSAdminPage'));
const DashboardWidgets = lazy(() => import('../DashboardWidgets/index'));

const Routing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useAppSelector(state => state.root.auth);
  const isProfileCompleted: boolean = getItem('isProfileCompleted') === 'true';

  useEffect(() => {
    const shouldRedirect = sessionStorage.getItem('redirectAfterReload');

    if (shouldRedirect) {
      sessionStorage.removeItem('redirectAfterReload');

      if (
        location.pathname.includes('/cloud-prep/tests') ||
        location.pathname.includes('/cloud-prep/instructions')
      ) {
        navigate('/cloud-prep', { replace: true });
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    const getRedirectPath = () => {
      if (isProfileCompleted) {
        return '/analytics';
      }
      return null;
    };

    const redirectPath = getRedirectPath();
    if (
      redirectPath &&
      ['/', '/otp', '/details/yearofpassing'].includes(location.pathname)
    ) {
      navigate(redirectPath, { replace: true });
    }
  }, [isProfileCompleted, location.pathname]);

  return (
    <Routes>
      <Route path='/' element={<LoginPage />} />
      <Route path='/login' element={<LoginPageV1 />} />

      <Route path='/otp' element={<Otp />} />
      <Route path='/details/:step' element={<OnboardingForm />} />
      <Route path='/oauth' element={<OAuthVerification />} />
      <Route path='/mock-test/tcs-nqt/:id' element={<MockTest />} />
      <Route
        path='/mock-results/:isMockTest/:id/:userMockTestId'
        element={<MockResults />}
      />
      <Route
        path='/no-access'
        element={
          <AuthenticatedLayout>
            <NoAccessPage />{' '}
          </AuthenticatedLayout>
        }
      />

      {/* AI Interview Routes - TECHNICAL_INTERVIEW (9002) */}
      <Route
        path='/ai'
        element={
          <AuthenticatedLayout>
            <AiDesign />
          </AuthenticatedLayout>
        }
      />
      <Route
        path='/ai-results'
        element={
          <AuthenticatedLayout>
            <InterviewResults />
          </AuthenticatedLayout>
        }
      />
      <Route
        path='/interview'
        element={
          <ProtectedRoute requiredDashboard={9002}>
            <AuthenticatedLayout>
              <SpeakingInterviewApp />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/interviewFullResults/:id/:userId'
        element={
          <AuthenticatedLayout>
            <InterviewResults />
          </AuthenticatedLayout>
        }
      />

      {/* HR Interview Routes - HR_INTERVIEW (9003) */}
      <Route
        path='/hr-interview'
        element={
          <ProtectedRoute requiredDashboard={9003}>
            <AuthenticatedLayout>
              <HRRound />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/hr-interview-results/:id/:userId'
        element={
          <AuthenticatedLayout>
            <HRInterviewResults />
          </AuthenticatedLayout>
        }
      />

      <Route
        path='/dashboard'
        element={
          <ProtectedRoute requiredDashboard={9001}>
            <AuthenticatedLayout>
              <DashboardPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path='/test'
        element={
          <AuthenticatedLayout>
            <PracticeTestLayout />
          </AuthenticatedLayout>
        }
      />
      <Route
        path='/custom-written-test'
        element={
          <ProtectedRoute requiredDashboard={9007}>
            <AuthenticatedLayout>
              <CreateTestScreen />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      {/* Programming Test Routes - PROGRAMMING_ROUND (9004) */}
      <Route
        path='/programming-test'
        element={
          <ProtectedRoute requiredDashboard={9004}>
            <AuthenticatedLayout>
              <ProgrammingTest />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/programming-test2'
        element={
          <ProtectedRoute requiredDashboard={9004}>
            <AuthenticatedLayout>
              <ProgrammingTestV2 />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/test-hub'
        element={
          <ProtectedRoute requiredDashboard={9001}>
            <AuthenticatedLayout>
              <AssignedTests />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      {/* CMS Routes - Can be protected based on your requirements */}
      <Route
        path='/admincms'
        element={
          <AuthenticatedLayout>
            <CMSAdminPage />
          </AuthenticatedLayout>
        }
      />
      <Route
        path='/datareviewercms'
        element={
          <AuthenticatedLayout>
            <CMSDataReviewerPage />
          </AuthenticatedLayout>
        }
      />
      <Route
        path='/dataentryspecialistcms'
        element={
          <AuthenticatedLayout>
            <CMSDataEntrySpecialistPage />
          </AuthenticatedLayout>
        }
      />
      <Route
        path='/addquestion'
        element={
          <AuthenticatedLayout>
            <AddQuestionPage />
          </AuthenticatedLayout>
        }
      />
      <Route
        path='/questionbank'
        element={
          <AuthenticatedLayout>
            <QuestionBankV2 />
          </AuthenticatedLayout>
        }
      />
      <Route
        path='/questionbank/:id'
        element={
          <AuthenticatedLayout>
            <QuestionBankPage />
          </AuthenticatedLayout>
        }
      />
      <Route
        path='/programming-categories'
        element={
          <ProtectedRoute requiredDashboard={9004}>
            <AuthenticatedLayout>
              <ProgrammingCategories />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/programming-category-results/:categoryId/:questionId'
        element={
          <AuthenticatedLayout>
            <ProgrammingCategoryResults />
          </AuthenticatedLayout>
        }
      />

      {/* Results & Reports Routes - OTHERS (9005) */}
      <Route
        path='/reports'
        element={
          <AuthenticatedLayout>
            <ReportsPage />
          </AuthenticatedLayout>
        }
      />
      <Route
        path='/results'
        element={
          <AuthenticatedLayout>
            <ResultsPage />
          </AuthenticatedLayout>
        }
      />
      <Route
        path='/results/:id/:userId'
        element={
          <AuthenticatedLayout>
            <TestResults />
          </AuthenticatedLayout>
        }
      />
      <Route
        path='/drive-results/:id/:userId'
        element={
          <AuthenticatedLayout>
            <DriveTestResult />
          </AuthenticatedLayout>
        }
      />
      <Route
        path='/payments'
        element={
          <AuthenticatedLayout>
            <PricingPlans />
          </AuthenticatedLayout>
        }
      />
      <Route
        path='/transaction-summary'
        element={
          // <AuthenticatedLayout>
          <TransactionSummary />
          // </AuthenticatedLayout>
        }
      />
      <Route
        path='/programming-results/:id/:userId'
        element={
          <AuthenticatedLayout>
            <ProgrammingResults />
          </AuthenticatedLayout>
        }
      />

      {/* Analytics & Audits - OTHERS (9005) */}
      <Route
        path='/analytics'
        element={
          <AuthenticatedLayout>
            <DashboardWidgets />
          </AuthenticatedLayout>
        }
      />
      <Route
        path='/user-audits'
        element={
          <AuthenticatedLayout>
            <UserAudits />
          </AuthenticatedLayout>
        }
      />
      <Route
        path='/website-leads'
        element={
          <AuthenticatedLayout>
            <WebsiteLeadsPage />
          </AuthenticatedLayout>
        }
      />
      <Route
        path='/cloud-prep'
        element={
          <ProtectedRoute requiredDashboard={9009}>
            <AuthenticatedLayout>
              <CloudPrepPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/cloud-prep-tabs/:id/:subId'
        element={
          <AuthenticatedLayout>
            <CloudPrepTabs />
          </AuthenticatedLayout>
        }
      />
      <Route
        path='/cloud-prep-tabs'
        element={
          <AuthenticatedLayout>
            <CloudPrepTabs />
          </AuthenticatedLayout>
        }
      />

      <Route
        path='/cloud-prep/tests/:testId'
        element={
          <AuthenticatedLayout>
            <TestScreen />
          </AuthenticatedLayout>
        }
      />

      <Route
        path='/cloud-prep/instructions/:testId'
        element={
          <AuthenticatedLayout>
            <CloudPrepInstructions />
          </AuthenticatedLayout>
        }
      />
      <Route
        path='/cloud-prep-results/:id/:userId/:redirect/'
        element={
          <AuthenticatedLayout>
            <TestResults isCloudPrepTest={true} />
          </AuthenticatedLayout>
        }
      />

      <Route
        path='/tenant-managment'
        element={
          <AuthenticatedLayout>
            <TenantAudit />
          </AuthenticatedLayout>
        }
      />
      <Route
        path='/students-audits'
        element={
          <AuthenticatedLayout>
            <StudentRecords />
          </AuthenticatedLayout>
        }
      />
      <Route
        path='/schedule-test'
        element={
          <AuthenticatedLayout>
            <ScheduleTestManagementDashboard />
          </AuthenticatedLayout>
        }
      />
      <Route
        path='/drive-test'
        element={
          <ProtectedRoute requiredDashboard={9008}>
            <AuthenticatedLayout>
              <DriveTestPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route path='/registration' element={<RegistrationPage />} />
      <Route path='/qr' element={<CampusRecruitmentPage />} />
      <Route
        path='/resume-builder-main-component'
        element={
          <AuthenticatedLayout>
            <ResumeBuilderMainComponent />
          </AuthenticatedLayout>
        }
      />
    </Routes>
  );
};

export default Routing;
